/**
 * Post-race recall quiz: questions built from the sentences the player just typed,
 * to test whether the content stuck. Deterministic given a seed so every player in a
 * race sees the same questions.
 */
import { mulberry32, SENTENCES } from './sentences.js';

export type QuizKind = 'blank' | 'which' | 'next';
export interface QuizQuestion {
  kind: QuizKind;
  prompt: string;
  /** The sentence the question is about, shown after answering. */
  source: string;
  options: string[];
  answer: number;
}

const STOP = new Set(
  'the a an and or but of to in on at for with is are was were be been it its this that these those you your we our they their he she his her not no do does did have has had will can could would should may might than then there here what which who whom when where why how all any each every some such only own same so too very just from by as into over after before about up down out off again further once more most other'.split(' '),
);

function words(s: string): string[] {
  return s.split(/\s+/).map((w) => w.replace(/^[^A-Za-z0-9']+|[^A-Za-z0-9']+$/g, '')).filter(Boolean);
}
function contentWords(s: string): string[] {
  return words(s).filter((w) => w.length >= 4 && !STOP.has(w.toLowerCase()) && !/^\d+$/.test(w));
}
function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
function pick<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]!;
}
function blankOut(sentence: string, word: string): string {
  const i = sentence.indexOf(word);
  return i < 0 ? sentence : `${sentence.slice(0, i)}_____${sentence.slice(i + word.length)}`;
}

/** Build up to `count` questions from the race sentences. `pool` supplies distractor sentences. */
export function makeQuiz(sentences: string[], seed: number, count = 3, pool: string[] = SENTENCES): QuizQuestion[] {
  const rng = mulberry32(seed);
  const out: QuizQuestion[] = [];
  const used = new Set<string>();
  const allWords = [...new Set(sentences.flatMap(contentWords))];
  // A queue of kinds to try; a kind that cannot be built from the material falls back
  // to 'which', which only needs distractor sentences from the pool.
  const kinds: QuizKind[] = shuffle(['blank', 'which', 'next'], rng);
  let guard = 0;
  while (out.length < count && guard++ < 30) {
    const kind = kinds.shift() ?? 'which';
    kinds.push(kind);
    const candidates = sentences.filter((s) => !used.has(s));
    if (!candidates.length) break;
    const s = pick(candidates, rng);
    if (kind === 'blank') {
      const cw = contentWords(s);
      const distractors = allWords.filter((w) => !cw.includes(w));
      if (cw.length === 0 || distractors.length < 3) {
        kinds.unshift('which');
        continue;
      }
      const word = pick(cw, rng);
      const opts = shuffle([word, ...shuffle(distractors, rng).slice(0, 3)], rng);
      out.push({ kind, prompt: `Fill the gap: "${blankOut(s, word)}"`, source: s, options: opts, answer: opts.indexOf(word) });
    } else if (kind === 'which') {
      const others = shuffle(pool.filter((x) => !sentences.includes(x)), rng).slice(0, 3);
      if (others.length < 3) break;
      const opts = shuffle([s, ...others], rng);
      out.push({ kind, prompt: 'Which of these sentences did you type in this race?', source: s, options: opts, answer: opts.indexOf(s) });
    } else {
      const w = words(s);
      if (w.length < 5) {
        kinds.unshift('which');
        continue;
      }
      const cut = 2 + Math.floor(rng() * Math.min(3, w.length - 3));
      const next = w[cut]!;
      const distractors = allWords.filter((x) => x.toLowerCase() !== next.toLowerCase() && !w.slice(0, cut + 1).includes(x));
      if (distractors.length < 3) {
        kinds.unshift('which');
        continue;
      }
      const opts = shuffle([next, ...shuffle(distractors, rng).slice(0, 3)], rng);
      out.push({ kind, prompt: `What came next? "${w.slice(0, cut).join(' ')} …"`, source: s, options: opts, answer: opts.indexOf(next) });
    }
    used.add(s);
  }
  return out;
}
